const toRgb = (color?: string) => {
  if (!color) return { r: 59, g: 130, b: 246 }

  if (/^#([0-9a-f]{6})$/i.test(color)) {
    const n = parseInt(color.slice(1), 16)
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
  }

  if (color.startsWith("rgb")) {
    const vals = color.match(/[\d.]+/g)?.map(Number) || [0, 0, 0]
    return { r: vals[0], g: vals[1], b: vals[2] }
  }

  return { r: 59, g: 130, b: 246 }
}

const toRgba = (color?: string) => {
  if (!color) return { r: 59, g: 130, b: 246, a: 1 }

  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(color)) {
    let hex = color.slice(1)

    if (hex.length === 3) {
      hex = hex
        .split("")
        .map(c => c + c)
        .join("")
    }

    const n = parseInt(hex, 16)
    return {
      r: (n >> 16) & 255,
      g: (n >> 8) & 255,
      b: n & 255,
      a: 1,
    }
  }

  // rgb() or rgba()
  if (color.startsWith("rgb")) {
    const vals = color.match(/[\d.]+/g)?.map(Number) || [0, 0, 0, 1]

    return {
      r: vals[0],
      g: vals[1],
      b: vals[2],
      a: vals[3] !== undefined ? vals[3] : 1,
    }
  }

  return { r: 59, g: 130, b: 246, a: 1 }
}

export function getContrastedColor(color: string): string {
  if (!color) return "#111827"
  const { r, g, b, a } = toRgba(color) as any

  if (a < 0.5) {
    return "#111827"
  }

  const yiq = (r * 299 + g * 587 + b * 114) / 1000
  return yiq >= 150 ? "#111827" : "#ffffff"
}

/**
 * Retourne 'black' ou 'white' selon le meilleur contraste
 * avec la couleur de fond donnée (hex ou rgb/rgba)
 */
export function getContrastingTextColor(bgColor: string): "black" | "white" {
  let r = 0,
    g = 0,
    b = 0

  // --- HEX ---
  if (bgColor.startsWith("#")) {
    const hex = bgColor.replace("#", "")
    const full =
      hex.length === 3
        ? hex
            .split("")
            .map(x => x + x)
            .join("")
        : hex
    const num = parseInt(full, 16)
    r = (num >> 16) & 255
    g = (num >> 8) & 255
    b = num & 255
  }
  // --- RGB / RGBA ---
  else if (bgColor.startsWith("rgb")) {
    const vals = bgColor.match(/[\d.]+/g)?.map(Number) || [0, 0, 0]
    ;[r, g, b] = vals
  }

  // Convertit en luminance relative (norme WCAG)
  const toLinear = (c: number) => {
    const v = c / 255
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  }
  const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)

  // Calcule les contrastes avec noir (#000) et blanc (#fff)
  const contrastBlack = (luminance + 0.05) / 0.05
  const contrastWhite = 1.05 / (luminance + 0.05)

  // Retourne la couleur de texte avec le meilleur contraste
  return contrastBlack > contrastWhite ? "black" : "white"
}
